/* eslint-disable max-lines */
import type { PageTargeting } from "../config/page";
import type { SlotName } from "../config/platforms/types";
import type { AdNode } from "./AdNode";

import invariant from "tiny-invariant";
import type { FandomCmp } from "common-types";
import type { GptPlatformPlugin } from "../plugins/gpt/GptPlatformPlugin";
import { UcpGptPlatformPlugin } from "../plugins/gpt/UcpGptPlatformPlugin";
import type { Hooks } from "./AdEnginePlugin";
import type { OutOfPageSlotConfig } from "../plugins/DynamicSlotPlugin/DynamicSlot.types";

/**
 * Page targeting retrieved from {@link googletag.PubAdsService.getTargeting}. This is separate from
 * the PageTargeting type here however since additonal values can be added by other scripts on the
 * page unknown to ad engine.
 *
 * In addition, the {@link googletag.PubAdsService.getTargeting} function doesn't return the same
 * structure, so when raw targeting is retrieved (from {@link GptWrapper.getTargeting},
 * {@link GptWrapper.getAllTargeting} with raw=true, or {@link GptWrapper.getSlotTargeting}) it will
 * always return with `string[]` instead of the original string | string[] input. So we need to
 * allow for keys of PageTargeting to also be `string[]`.
 */
export type GptPageTargeting = {
  [key: keyof PageTargeting | string]: string | string[];
};
/**
 * Options to pass into the {@link GptWrapper.getAllTargeting} function.
 */
export type GptWrapperGetAllTargetingOptions = {
  /**
   * When true, the returned keyvalues will be returned as string[] as they are from
   * {@link googletag.PubAdsService.getTargeting} without being sanitized.
   */
  raw?: boolean;
};

type GptWrapperHooks = Pick<Required<Hooks>, "onBeforeAdsRequest">;

const LAST_KEY_VAL_NAME = "fs";

export class GptWrapper {
  private readonly GAM_ID = "5441";
  // Todo: use the current platform to create this instead of hard coding so we can use the correct
  // gpt plugin for the current platform
  private readonly gptPlatformPlugin: GptPlatformPlugin;

  constructor(
    private readonly initialPageTargeting: PageTargeting,
    private readonly hooks: GptWrapperHooks,
  ) {
    this.gptPlatformPlugin = new UcpGptPlatformPlugin(this);
    window.googletag = window.googletag ?? {
      cmd: [] as googletag.CommandArray,
    };
    this.gptPlatformPlugin.setupPerformanceMarks(this);
  }

  async enableServices() {
    return new Promise<void>((resolve) => {
      this.addToCommandQueue(() => {
        googletag.pubads().disableInitialLoad();
        googletag.pubads().enableSingleRequest();
        googletag.pubads().enableVideoAds();

        // Add all page targeting
        for (const [key, value] of Object.entries(this.initialPageTargeting)) {
          this.setTargeting(key, value);
        }

        googletag.enableServices();

        resolve();
      });
    });
  }

  /**
   * Returns the current Google Ad Manager ID
   * @returns the ID for Google Ad Manager
   */
  getGamId() {
    return this.GAM_ID;
  }

  /**
   * Updates the page settings configuration for Google Publisher Tag (GPT).
   * @param {googletag.config.PageSettingsConfig} config - The configuration object for the page settings.
   */
  setConfig(config: googletag.config.PageSettingsConfig) {
    this.addToCommandQueue(() => {
      googletag.setConfig(config);
    });
  }

  /**
   * Sets a targeting key-value pair for the Google Publisher Tag (GPT).
   *
   * @param {string} key - The targeting key.
   * @param {string | string[]} value - The targeting value(s).
   */
  setTargeting(key: string, value: string | string[]) {
    if (typeof value === "string" || Array.isArray(value)) {
      googletag.pubads().setTargeting(key, value);
      return;
    }
    // Silently fail if the value is not a string or an array of strings, even google fails gracefully

    console.error(
      `Targeting value for key "${key}" must be a string or an array of strings, passed `,
      value,
    );
  }

  /**
   * Retrieves the targeting values associated with a key from the Google Publisher Tag (GPT).
   *
   * @param {string} key - The targeting key.
   * @returns {string[]} - The targeting values associated with the key.
   */
  getTargeting(key: string): string[] {
    return googletag.pubads().getTargeting(key);
  }

  /**
   * Clears the targeting value associated with the specified key for the Google Publisher Tag (GPT).
   *
   * @param {string} key - The targeting key to be cleared.
   */
  clearTargeting(key: string): void {
    googletag.pubads().clearTargeting(key);
  }

  /**
   * Returns the Ad Unit Path for the provided slot. If the slot is not defined, then this will
   * return null.
   *
   * @param slotName The name/id of the slot on the page
   * @returns The ad unit path for that slot from gpt library
   */
  getAdUnitPath(slotName: SlotName): string | null {
    const slots = this.getGPTSlotsBySlotNames([slotName]);
    if (slots.length >= 1) {
      return slots[0].getAdUnitPath();
    }

    return null;
  }

  /**
   * Retrieves all targeting from Google Publisher Tag (GPT). These are the page targeting values
   * set on the `cust_params` parameter of the ad request.
   *
   * If options.raw is set to true, then the values are returned in the format Gpt returns them.
   * GPT always returns targeting values as a `string[]`, so even targeting added as a single
   * string will be returned as a string[] of length 1. Empty values will be an empty array `[]`.
   *
   * When raw is set to false, or not provided, it will default to false and the santized values
   * will be returned where empty arrays are omitted from the returned object, single values are
   * unwrapped to string, and multiple values stay as arrays.
   *
   * For example, the following will show the same targeting object returned with the different raw
   * options:
   *
   * No options, or raw = false:
   * ```ts
   * {
   *   s0: "ent",
   *   s0c: ["ent", "tv", "videogames", "books", "comics", "movies"],
   *   s1: "_starwars",
   *   artid: "1234"
   * }
   * ```
   *
   * raw=true
   * ```ts
   * {
   *   s0: ["ent"],
   *   s0c: ["ent", "tv", "videogames", "books", "comics", "movies"],
   *   s1: ["_starwars"],
   *   artid: ["1234"],
   *   emptyValue: []
   * }
   * ```
   *
   * @return Object with all page keyvalues.
   */
  getAllTargeting(
    options: GptWrapperGetAllTargetingOptions = {},
  ): GptPageTargeting {
    const { raw = false } = options;
    const targeting: GptPageTargeting = {};
    const targetingKeys = googletag.pubads().getTargetingKeys();
    for (const key of targetingKeys) {
      const value = this.getTargeting(key);

      if (raw || value.length > 1) {
        targeting[key] = value;
      } else if (value.length === 1) {
        targeting[key] = value[0];
      }
    }
    return targeting;
  }

  /**
   * Sets the Publisher Provided Identifier (PPID) for Google Publisher Tag (GPT).
   * and updates both the `ppid` targeting key.
   *
   * @param ppid - The Publisher Provided Identifier to be set.
   */
  updatePublisherProvidedId(ppid: string) {
    if (!ppid) {
      return;
    }

    this.addToCommandQueue(() => {
      this.setTargeting("ppid", ppid);
      googletag.pubads().setPublisherProvidedId(ppid);
    });
  }

  /**
   * Called when privacy has updated to set the googletag privacy settings and update the privacy
   * keyvalues `npa` and `rdp`.
   */
  updatePrivacySettings({ consentApi, optOut, coppa }: FandomCmp) {
    const npa = consentApi === "tcf" && optOut ? "1" : "0";
    const rdp = consentApi === "usp" && optOut ? "1" : "0";
    const shouldAttachMonetizationParam =
      window.fandomContext?.site?.directedAtChildren;

    this.addToCommandQueue(() => {
      if (shouldAttachMonetizationParam) {
        this.setTargeting("monetization", coppa ? "restricted" : "regular");
      }

      this.setTargeting("npa", npa);
      this.setTargeting("rdp", rdp);
      googletag.pubads().setPrivacySettings({
        nonPersonalizedAds: npa === "1",
        restrictDataProcessing: rdp === "1",
        childDirectedTreatment: coppa === true,
      });
    });
  }

  /**
   * Defines an outOfPage slot for the provided ad node.
   * By default, if there is no format specified from {@link OutOfPageSlotConfig.format},
   * then we'll default to "INTERSTITIAL" format.
   * */
  private defineOutOfPageSlot(
    adNode: AdNode,
    adUnitPath: string,
    outOfPageConfig: OutOfPageSlotConfig,
  ): googletag.Slot | null {
    // If we ever want to call defineOutOfPageSlot with a div,
    // we can remove the defaulting of format to "INTERSTITIAL".
    // googletag.defineOutOfPageSlot also accepts a div name as the second parameter.
    const { format = "INTERSTITIAL" } = outOfPageConfig ?? {};
    // Define the slot using googletag's defineOutOfPageSlot method,
    // and pass in and outOfPageFormat if provided, otherwise pass in the slot name
    // which is also the div which the ad slot will be attached to
    const slot = googletag.defineOutOfPageSlot(
      adUnitPath,
      googletag.enums.OutOfPageFormat[format] ?? adNode.getSlotConfig().name,
    );

    // If we got a slot, update the slotName to the <div> that google created.
    if (slot !== null) {
      adNode.setOutOfPageSlotName(slot?.getSlotElementId() as SlotName);
    }

    return slot;
  }

  /**
   * Defines the gptSlot for the provided ad node.
   * The slot will be defined either as a regular slot with sizes attached to them
   * or an outOfPage slot, with no sizes attached and with an optional outOfPageFormat.
   *
   * @see {@link https://developers.google.com/publisher-tag/reference#googletag.defineSlot}
   * @see {@link https://developers.google.com/publisher-tag/reference#googletag.defineOutOfPageSlot}
   * */
  defineSlot(adNode: AdNode): void {
    this.addToCommandQueue(() => {
      // Get the slot configuration and ad unit path for the ad node
      const slotConfig = adNode.getSlotConfig();
      const adUnitPath = this.gptPlatformPlugin.buildAdUnitPathForNode(adNode);

      let slot: googletag.Slot | null = null;
      const outOfPageConfig = slotConfig.dynamic?.outOfPage;

      if (outOfPageConfig) {
        slot = this.defineOutOfPageSlot(adNode, adUnitPath, outOfPageConfig);
      } else {
        // Define the slot using googletag's defineSlot method
        slot = googletag.defineSlot(
          adUnitPath,
          slotConfig.sizes,
          slotConfig.name,
        );
      }

      // If the slot is not defined, log a warning and return
      // This can happen if the slot configuration is incorrect or if the ad unit path is invalid
      // In such cases, we should not proceed with further operations on the slot
      if (slot === null) {
        console.warn("GptWrapper: Slot is null", slotConfig);
        return;
      }

      // Set the slot targeting and size mapping
      Object.entries(slotConfig.targeting).forEach(([key, value]) => {
        slot.setTargeting(key, value);
      });

      if (slotConfig.responsiveSizes && !outOfPageConfig) {
        slot.defineSizeMapping(slotConfig.responsiveSizes);
      }

      // Add the slot to the googletag services
      slot.addService(googletag.pubads());

      // If the slot has companion ads, add the companion ads service
      if (slotConfig.companion) {
        slot.addService(googletag.companionAds());
      }
    });
  }

  private getGPTSlotsByAdNodes(adNodes: AdNode[]) {
    return this.getGPTSlotsBySlotNames(
      adNodes.map((adNode) => adNode.getSlotName()),
    );
  }

  private getGPTSlotsBySlotNames(slotNames: SlotName[]) {
    const slotNamesSet = new Set(slotNames);

    return googletag
      .pubads()
      .getSlots()
      .filter((slot) => slotNamesSet.has(slot.getSlotElementId() as SlotName));
  }

  /**
   * Sets LAST_KEY_VAL_NAME key to 't', removing it first so it's the LAST parameter.
   * Used to detect if the GAM request was truncated (missing LAST_KEY_VAL_NAME = truncated).
   * Due to possible race conditions, this keyval is exceptionally added in GptWrapper
   */
  private addKeyValForTruncationCheck() {
    this.clearTargeting(LAST_KEY_VAL_NAME);
    this.setTargeting(LAST_KEY_VAL_NAME, "t");
  }

  requestSlots(adNodes: AdNode[]) {
    this.hooks.onBeforeAdsRequest(adNodes);
    this.addKeyValForTruncationCheck();
    googletag.pubads().refresh(this.getGPTSlotsByAdNodes(adNodes));
  }

  destroySlots(adNodes?: AdNode[]) {
    if (adNodes) {
      googletag.destroySlots(this.getGPTSlotsByAdNodes(adNodes));
    }
  }

  setSlotTargeting(slotName: SlotName, key: string, value: string | string[]) {
    const [slot] = this.getGPTSlotsBySlotNames([slotName]);

    invariant(slot, `Slot ${slotName} not found`);

    slot.setTargeting(key, value);
  }

  getSlotTargeting(slotName: SlotName, key: string) {
    const [slot] = this.getGPTSlotsBySlotNames([slotName]);

    invariant(slot, `Slot ${slotName} not found`);

    return slot.getTargeting(key);
  }

  getAllSlotTargeting(slotName: SlotName): Record<string, string[]>;
  getAllSlotTargeting(
    slotName: SlotName,
    options?: GptWrapperGetAllTargetingOptions,
  ): Record<string, string | string[]>;
  getAllSlotTargeting(
    slotName: SlotName,
    options: GptWrapperGetAllTargetingOptions = {},
  ): Record<string, string | string[]> {
    const { raw = true } = options;

    const [slot] = this.getGPTSlotsBySlotNames([slotName]);
    invariant(slot, `Slot ${slotName} not found`);

    const targetingKeys = slot.getTargetingKeys();
    return Object.fromEntries(
      targetingKeys.map((key) => {
        let value: string | string[] = slot.getTargeting(key);

        // Unwrap single-element array to string when raw is false for easier consumption of targeting values
        if (!raw && Array.isArray(value) && value.length === 1) {
          value = value[0];
        }

        return [key, value];
      }),
    );
  }

  updateSizeMapping(
    slotName: SlotName,
    newSizeMapping: googletag.SizeMappingArray,
  ) {
    const [slot] = this.getGPTSlotsBySlotNames([slotName]);

    invariant(slot, `Slot ${slotName} not found`);

    return slot.defineSizeMapping(newSizeMapping);
  }

  addToCommandQueue(callback: () => void) {
    googletag.cmd.push(callback);
  }

  addEventListener<T extends keyof googletag.events.EventTypeMap>(
    event: T,
    callback: (event: googletag.events.EventTypeMap[T]) => void,
  ) {
    googletag.pubads().addEventListener(event, callback);
  }

  removeEventListener<T extends keyof googletag.events.EventTypeMap>(
    event: T,
    callback: (event: googletag.events.EventTypeMap[T]) => void,
  ) {
    googletag.pubads().removeEventListener(event, callback);
  }

  /**
   * Attaches an event listener to the gpt wrapper for the provided event but only calls the
   * callback when the slot in the event matches the provided slot name.
   *
   * You can remove the event listener by calling the returned function
   *
   * @param slotName The name of the slot to listen for events on
   * @param event The event to listen for on the gpt wrapper
   * @param callback The callback to call when the event is fired and the slot in the event
   * matches the provided slot name
   * @returns Function that when called will remove the event listener
   */
  addSlotEventListener<T extends keyof googletag.events.EventTypeMap>(
    slotName: SlotName,
    event: T,
    callback: (event: googletag.events.EventTypeMap[T]) => void,
  ): () => void {
    const cb = (event: googletag.events.EventTypeMap[T]) => {
      const slot = event.slot;
      if (slot.getSlotElementId() === slotName) {
        callback(event);
      }
    };
    this.addEventListener(event, cb);

    return () => this.removeEventListener(event, cb);
  }
}
